import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { toast } from "react-toastify";

const MessageContext = createContext();

export const useMessage = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessage must be used within a MessageProvider");
  }
  return context;
};

const API_BASE = process.env.REACT_APP_API_URL;

const MESSAGE_TYPES = [
  { label: "Text", value: "text" },
  { label: "Image", value: "image" },
  { label: "Document", value: "document" },
  { label: "Video", value: "video" },
];

const LANGUAGES = [
  "PHP",
  "Node.js",
  "JavaScript",
  "Python",
  "Java",
  "C#",
  "Go",
];

export const MessageProvider = ({
  children,
  instanceId,
  authToken,
  isLoggedIn,
}) => {
  // Form State
  const [messageForm, setMessageForm] = useState({
    phoneNumber: "",
    message: "",
  });

  // UI State
  const [sending, setSending] = useState(false);
  const [sendingUpload, setSendingUpload] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("PHP");
  const [messageType, setMessageType] = useState("text");

  // Media State
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [caption, setCaption] = useState("");

  // Variable Inputs States for message & caption
  const [variableValues, setVariableValues] = useState({});
  const [captionVariableValues, setCaptionVariableValues] = useState({});

  // Extract unique variable names from template string like {{var}}
  const extractVariables = (template) => {
    const regex = /{{\s*([\w]+)\s*}}/g;
    const vars = new Set();
    let match;
    while ((match = regex.exec(template)) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  };

  // Replace variables with user values for preview only
  const replaceVariables = (template, values) => {
    return template.replace(/{{\s*([\w]+)\s*}}/g, (_, v) =>
      values[v] !== undefined ? values[v] : `{{${v}}}`
    );
  };

  // Extract variables from message and caption templates
  const variables = useMemo(
    () => extractVariables(messageForm.message),
    [messageForm.message]
  );
  const captionVariables = useMemo(() => extractVariables(caption), [caption]);

  // Reset variableValues preserving existing for message variables
  useEffect(() => {
    setVariableValues((prev) => {
      const newVals = {};
      variables.forEach((v) => {
        newVals[v] = prev[v] !== undefined ? prev[v] : "";
      });
      return newVals;
    });
  }, [variables]);

  // Reset captionVariableValues preserving existing for caption variables
  useEffect(() => {
    setCaptionVariableValues((prev) => {
      const newVals = {};
      captionVariables.forEach((v) => {
        newVals[v] = prev[v] !== undefined ? prev[v] : "";
      });
      return newVals;
    });
  }, [captionVariables]);

  // Clear variables & caption variables and caption text on messageType change
  useEffect(() => {
    setVariableValues({});
    setCaptionVariableValues({});
    if (messageType !== "text") {
      setCaption("");
    }
  }, [messageType]);

  // Handlers for variable input changes
  const handleVariableChange = useCallback((varName, value) => {
    setVariableValues((prev) => ({ ...prev, [varName]: value }));
  }, []);

  const handleCaptionVariableChange = useCallback((varName, value) => {
    setCaptionVariableValues((prev) => ({ ...prev, [varName]: value }));
  }, []);

  // Message form changes
  const handlePhoneNumberChange = useCallback((e) => {
    setMessageForm((prev) => ({ ...prev, phoneNumber: e.target.value }));
  }, []);
  const handleMessageChange = useCallback((e) => {
    setMessageForm((prev) => ({ ...prev, message: e.target.value }));
  }, []);
  const handleCaptionChange = useCallback(
    (e) => setCaption(e.target.value),
    []
  );

  // Reset media and caption variables
  const resetMediaFields = useCallback(() => {
    setMediaFile(null);
    setMediaUrl("");
    setCaption("");
    setCaptionVariableValues({});
  }, []);

  // Message type change handler clears media and caption variables
  const handleMessageTypeChange = useCallback(
    (e) => {
      setMessageType(e.target.value);
      resetMediaFields();
    },
    [resetMediaFields]
  );

  // File change and upload handlers unchanged
  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (
        messageType === "image" &&
        ![
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/gif",
          "image/webp",
        ].includes(file.type)
      ) {
        toast.error("Only image files allowed for 'Image' message type");
        e.target.value = "";
        return;
      }
      if (messageType === "document" && file.type !== "application/pdf") {
        toast.error("Only PDF files allowed for 'Document' message type");
        e.target.value = "";
        return;
      }
      setMediaFile(file);
    },
    [messageType]
  );

  const handleUpload = useCallback(async () => {
    if (!mediaFile) return;
    setSendingUpload(true);
    try {
      const formData = new FormData();
      formData.append("document", mediaFile);

      const token = localStorage.getItem("userAccessToken") || authToken || "";
      const response = await fetch(`${API_BASE}/api/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!data.success) {
        toast.error(data.message || "Failed to upload file. Please try again.");
        setMediaUrl("");
        setSendingUpload(false);
        return;
      }
      setMediaUrl(data.data.doc_url);
      toast.success("File uploaded successfully!");
    } catch {
      toast.error("Upload failed. Please retry.");
      setMediaUrl("");
    } finally {
      setSendingUpload(false);
    }
  }, [mediaFile, authToken]);

  // Preview variables substitution for message & caption
  const previewMessage = useMemo(
    () =>
      variables.length > 0
        ? replaceVariables(messageForm.message, variableValues)
        : messageForm.message,
    [messageForm.message, variableValues, variables]
  );
  const previewCaption = useMemo(
    () =>
      captionVariables.length > 0
        ? replaceVariables(caption, captionVariableValues)
        : caption,
    [caption, captionVariableValues, captionVariables]
  );

  // Send message: send original templates + variables maps
  const handleSendMessage = useCallback(async () => {
    setSendResult(null);

    if (!messageForm.phoneNumber.trim()) {
      setSendResult({ success: false, message: "Please enter a phone number" });
      toast.error("Enter phone number");
      return;
    }
    if (!authToken) {
      setSendResult({
        success: false,
        message: "Authentication token not found.",
      });
      toast.error("Authentication token missing");
      return;
    }
    if (!isLoggedIn) {
      setSendResult({ success: false, message: "WhatsApp not connected." });
      toast.error("Connect WhatsApp first");
      return;
    }
    if (messageType === "text" && !messageForm.message.trim()) {
      setSendResult({
        success: false,
        message: "Please enter a message template",
      });
      toast.error("Please enter a message template");
      return;
    }
    if (messageType !== "text" && !mediaUrl) {
      setSendResult({
        success: false,
        message: "Please upload your file first",
      });
      toast.error("Upload file first");
      return;
    }

    setSending(true);

    const postBody = {
      instanceId,
      phoneNumber: messageForm.phoneNumber,
      message_type: messageType,
    };

    if (messageType === "text") {
      postBody.message = messageForm.message;
      postBody.variables = variableValues;
    } else {
      postBody.caption = caption;
      postBody.caption_variables = captionVariableValues;
      postBody.media_url = mediaUrl;
    }

    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(postBody),
      });
      const data = await response.json();

      setSendResult({
        success: data.success,
        message:
          data.message ||
          (data.success
            ? "Message sent successfully!"
            : "Failed to send message"),
      });

      if (data.success) {
        toast.success("Message sent successfully!");
        setMessageForm({ phoneNumber: "", message: "" });
        resetMediaFields();
        setMessageType("text");
      } else {
        toast.error(data.message || "Failed to send message");
      }
    } catch {
      setSendResult({
        success: false,
        message: "Network error: Failed to send message",
      });
      toast.error("Network error occurred");
    } finally {
      setSending(false);
    }
  }, [
    messageForm,
    authToken,
    isLoggedIn,
    messageType,
    mediaUrl,
    caption,
    captionVariableValues,
    instanceId,
    resetMediaFields,
    variableValues,
  ]);

  // Clipboard copy
  const copyToClipboard = useCallback(async (text, label = "Code") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error("Failed to copy");
    }
  }, []);

  // API docs generator including caption_variables
  const getCodeExample = useMemo(() => {
    const mp = {
      instanceId,
      phoneNumber: messageForm.phoneNumber || "919876543210",
      message_type: messageType,
    };

    if (messageType === "text") {
      mp.message = messageForm.message || "Hello {{name}}";
      mp.variables = variableValues;
    } else {
      mp.message = caption || "Caption here";
      mp.caption_variables = captionVariableValues;
      mp.media_url = mediaUrl || "https://example.com/file.pdf";
    }

    const variablesJson = mp.variables
      ? JSON.stringify(mp.variables, null, 2)
      : "{}";
    const captionVariablesJson = mp.caption_variables
      ? JSON.stringify(mp.caption_variables, null, 2)
      : "{}";

    const PHP_FIELDS = `'instanceId' => '${mp.instanceId}',
    'phoneNumber' => '${mp.phoneNumber}',
    'message_type' => '${mp.message_type}',
    'message' => '${mp.message}',
    'variables' => ${variablesJson},${
      mp.caption_variables
        ? `'caption_variables' => ${captionVariablesJson},`
        : ""
    }
    ${mp.media_url ? `'media_url' => '${mp.media_url}',` : ""}`;

    const JS_FIELDS = `instanceId: '${mp.instanceId}',
    phoneNumber: '${mp.phoneNumber}',
    message_type: '${mp.message_type}',
    message: \`${mp.message}\`,
    variables: ${variablesJson},${
      mp.caption_variables ? `caption_variables: ${captionVariablesJson},` : ""
    }
    ${mp.media_url ? `media_url: '${mp.media_url}',` : ""}`;

    return (
      {
        PHP: `<?php
$data = array(
    ${PHP_FIELDS}
);
$curl = curl_init();
curl_setopt_array($curl, array(
    CURLOPT_URL => "${API_BASE}/api/whatsapp/send-message",
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_ENCODING => "",
    CURLOPT_MAXREDIRS => 10,
    CURLOPT_TIMEOUT => 30,
    CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
    CURLOPT_CUSTOMREQUEST => "POST",
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_HTTPHEADER => array(
        "Authorization: Bearer ${authToken || "YOUR_TOKEN_HERE"}",
        "Content-Type: application/json"
    ),
));
$response = curl_exec($curl);
$err = curl_error($curl);
curl_close($curl);
if ($err) { echo "cURL Error #:" . $err; } else { echo $response; }`,

        JavaScript: `fetch('${API_BASE}/api/whatsapp/send-message', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${authToken || "YOUR_TOKEN_HERE"}',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    instanceId: '${mp.instanceId}',
    phoneNumber: '${mp.phoneNumber}',
    message_type: '${mp.message_type}',
    message: \`${mp.message}\`,
    variables: ${variablesJson},${
          mp.caption_variables
            ? `caption_variables: ${captionVariablesJson},`
            : ""
        }
    ${mp.media_url ? `'media_url': '${mp.media_url}',` : ""}
  })
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`,
      }[selectedLanguage] || ""
    );
  }, [
    selectedLanguage,
    instanceId,
    messageForm,
    messageType,
    caption,
    mediaUrl,
    authToken,
    variableValues,
    captionVariableValues,
  ]);

  const contextValue = {
    MESSAGE_TYPES,
    LANGUAGES,
    messageForm,
    sending,
    sendingUpload,
    sendResult,
    selectedLanguage,
    messageType,
    mediaFile,
    mediaUrl,
    caption,
    variableValues,
    variables,
    captionVariableValues,
    captionVariables,
    previewMessage,
    previewCaption,
    handlePhoneNumberChange,
    handleMessageChange,
    handleCaptionChange,
    handleMessageTypeChange,
    handleFileChange,
    handleUpload,
    handleSendMessage,
    copyToClipboard,
    resetMediaFields,
    handleVariableChange,
    handleCaptionVariableChange,
    setSelectedLanguage,
    getCodeExample,
    instanceId,
    authToken,
    isLoggedIn,
  };

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
};
